// Shared report page chrome — date-range preset chips + back-link.
//
// Date presets: Last 7d / Last 30d (default) / This month (MTD) / Last
// month / This quarter (QTD) / Year to date — plus your own From / To dates
// (Stephanie 2026-10-16: "there also needs to be a way to set your own custom
// range"). The range and rep live in the address bar (?range=…&from=…&to=…&rep=…)
// so a report can be bookmarked or shared and survives Back from an order.
//
// `from` + `to` are ISO YYYY-MM-DD strings — date-only, no time. Server
// expands to T00:00:00 / T23:59:59 boundaries.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

import {
  PRESET_LABELS,
  presetRange,
  readReportParams,
  validCustomRange,
  type DateRange,
  type RangePreset,
} from "@/lib/report-range";

export { presetRange, readReportParams, validCustomRange };
export type { DateRange, RangePreset };

interface ReportShellProps {
  title: string;
  subtitle?: string;
  preset: RangePreset;
  onPresetChange: (p: RangePreset) => void;
  range: DateRange;
  /** Your own dates. Given → the From / To boxes show. */
  onCustomRange?: (r: DateRange) => void;
  /** Given → a Rep picker shows ("" = everyone). */
  repId?: string;
  onRepChange?: (repId: string) => void;
  children: React.ReactNode;
  fetchedAt?: string;
  /** Full page width (CEO prefers wide report layouts). */
  wide?: boolean;
}

export function ReportShell({
  title,
  subtitle,
  preset,
  onPresetChange,
  range,
  onCustomRange,
  repId,
  onRepChange,
  children,
  fetchedAt,
  wide,
}: ReportShellProps) {
  return (
    <div className={wide ? "mx-auto w-full" : "mx-auto max-w-6xl"}>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/reports">
          <ArrowLeft className="h-4 w-4" /> All reports
        </Link>
      </Button>

      <div className="mb-6">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Reports
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2 max-w-2xl text-sm text-brown-400">{subtitle}</p>
        )}
      </div>

      {/* Range chips + your own dates + rep */}
      <div className="mb-6 flex flex-wrap items-end gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Range
          </span>
          {(Object.keys(PRESET_LABELS) as Array<Exclude<RangePreset, "custom">>).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPresetChange(p)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors",
                preset === p
                  ? "bg-brown-500 text-cream-50"
                  : "bg-cream-100 text-brown-400 hover:bg-cream-200",
              )}
            >
              {PRESET_LABELS[p]}
            </button>
          ))}
          {!onCustomRange && (
            <span className="ml-2 text-xs text-brown-300">
              {range.from} → {range.to}
            </span>
          )}
        </div>
        {onCustomRange && <CustomRange range={range} active={preset === "custom"} onApply={onCustomRange} />}
        {onRepChange && <RepPicker value={repId ?? ""} onChange={onRepChange} />}
      </div>

      {children}

      {fetchedAt && (
        <p className="mt-8 text-xs text-brown-300">
          As of {new Date(fetchedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}

const DATE_INPUT =
  "h-9 rounded-md border border-rule bg-white px-2 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]";

function CustomRange({ range, active, onApply }: { range: DateRange; active: boolean; onApply: (r: DateRange) => void }) {
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  // Picking a preset fills the boxes with its dates.
  useEffect(() => {
    setFrom(range.from);
    setTo(range.to);
  }, [range.from, range.to]);
  const ok = validCustomRange(from, to);
  const changed = from !== range.from || to !== range.to;
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok) onApply({ from, to });
      }}
    >
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">From</span>
        <input type="date" aria-label="From date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={cn(DATE_INPUT, active && "border-brown-500")} />
      </label>
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">To</span>
        <input type="date" aria-label="To date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={cn(DATE_INPUT, active && "border-brown-500")} />
      </label>
      <Button type="submit" size="sm" variant={changed ? "primary" : "outline"} disabled={!ok || !changed}>
        Apply
      </Button>
      {!ok && <span className="pb-2 text-xs text-rust-600">From must be on or before To.</span>}
    </form>
  );
}

function RepPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const repsQ = useQuery({
    queryKey: ["order-reps"],
    queryFn: () => api<{ reps: Array<{ id: string; name: string }> }>("/order-reps"),
    staleTime: 5 * 60_000,
  });
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Rep</span>
      <select aria-label="Rep" value={value} onChange={(e) => onChange(e.target.value)} className={cn(DATE_INPUT, "pr-8")}>
        <option value="">Everyone</option>
        {(repsQ.data?.reps ?? []).map((r) => (
          <option key={r.id} value={r.id}>{r.name}</option>
        ))}
      </select>
    </label>
  );
}

/**
 * Convenience hook: the current preset, the resolved range, the rep, and
 * setters — all kept in the address bar.
 */
export function useReportRange(initial: Exclude<RangePreset, "custom"> = "last-30") {
  const [params, setParams] = useSearchParams();
  const { preset, range, repId } = readReportParams(params, initial);
  const update = (fn: (p: URLSearchParams) => void) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        fn(next);
        return next;
      },
      { replace: true },
    );
  const setPreset = (p: RangePreset) =>
    update((n) => {
      if (p === "custom") return;
      n.set("range", p);
      n.delete("from");
      n.delete("to");
    });
  const setCustom = (r: DateRange) =>
    update((n) => {
      n.set("range", "custom");
      n.set("from", r.from);
      n.set("to", r.to);
    });
  const setRepId = (id: string) =>
    update((n) => {
      if (id) n.set("rep", id);
      else n.delete("rep");
    });
  return { preset, setPreset, range, setCustom, repId, setRepId };
}
